/**
 * Shared marketing brand tokens.
 *
 * The cyan/Barlow/dark-palette constants and the logo/testimonial/step arrays
 * were historically copy-pasted across Home.tsx, LocationPage.tsx and
 * IndustryPage.tsx. This module centralizes the ones the landing-page template
 * needs so new pages stay visually consistent with the marketing site.
 */

export const ff = "'Barlow', sans-serif";
export const cyan = "#57d7e2";
export const cyanBright = "#3aefff";
export const ctaGradient = "linear-gradient(135deg,#85e2e9,#20b0bc)";
export const pillRadius = "88px";

export const bg = {
  page: "#0a0c0c",
  panel: "#0c0c0c",
  deep: "#080a0a",
  card: "#111111",
} as const;

export const border = {
  faint: "rgba(255,255,255,0.07)",
  soft: "rgba(255,255,255,0.10)",
} as const;

export const CDN =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";

/**
 * The Indexed wordmark, served from our own origin.
 *
 * It used to be pulled from the CDN prefix above; that copy stopped resolving
 * and left a broken-image placeholder where the logo should be, in the nav and
 * footer of every page. The mark is our own asset and sits on the critical
 * path of the first paint, so it now lives in `client/public/` — nothing else
 * has to stay up for the logo to render.
 *
 * 336x52 source; size it with explicit width/height at each call site.
 */
export const INDEXED_WORDMARK = "/indexed-wordmark.webp";

export const LOGO_ASSETS = [
  `/4941-9030.webp`, `/4941-9031.webp`, `/4941-9032.webp`, `/4941-9033.webp`,
  `/4941-9034.webp`, `/4941-9035.webp`, `/4941-9036.webp`, `/4941-9037.webp`,
  `/4941-9038.webp`, `/4941-9039.webp`, `/4941-9040.webp`, `/4941-9041.webp`,
  `/4941-9042.webp`, `/4941-9043.webp`, `/4941-9044.webp`, `/4941-9045.webp`,
  `/4941-9046.webp`, `/4941-9047.webp`, `/4941-9048.webp`, `/4941-9049.webp`,
  `/4941-9050.webp`, `/4941-9051.webp`, `/4941-9052.webp`, `/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",
];

export interface BrandTestimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
}

export const TESTIMONIALS: BrandTestimonial[] = [
  { quote: "Exceptional communication throughout the project. The team showed a deep grasp of our goals and delivered a flawless final product that exceeded our expectations.", name: "Flavio", role: "Growth Marketing Director", company: "B2B SaaS" },
  { quote: "Great team to work with, very happy with the consultancy and final documents. They translated complex SEO strategy into something our whole team could act on.", name: "Kirsten", role: "Senior Marketing Manager", company: "Professional Services" },
  { quote: "Indexed turned SEO from a cost center into our highest-ROI acquisition channel. The pipeline numbers speak for themselves — $1.5m in 10 months.", name: "James", role: "CEO", company: "Renewable Energy" },
  { quote: "The level of technical depth was unlike anything I'd seen from an SEO agency. They found issues our in-house team had missed for years and fixed them within weeks.", name: "Sarah", role: "Head of Digital", company: "FinTech" },
  { quote: "We went from page 3 to position 1 for our core terms in under 5 months. The revenue attribution reporting made it easy to justify the investment to our board.", name: "Marcus", role: "VP Marketing", company: "Enterprise SaaS" },
  { quote: "Indexed didn't just improve our rankings — they rebuilt how we think about search. The GEO work alone has opened up a channel we didn't even know existed.", name: "Priya", role: "CMO", company: "Biotech" },
  { quote: "Search went from an afterthought to our most scalable acquisition channel. We grew from 150 to 920 daily organic clicks across our locations in just over a year.", name: "Daniel", role: "Marketing Director", company: "Storage & Real Estate" },
];

/**
 * Named clients with photos — the roster the site should be showing.
 *
 * TESTIMONIALS above is the older anonymous set ("Flavio · B2B SaaS"). These
 * carry a real person, a real company and a face, which is the difference
 * between a quote someone believes and one they skim. Pages are being moved
 * over to these; the anonymous set stays until the last one is converted.
 *
 * `photo` and `logo` are optional so a quote can go live the moment we have
 * permission for the words, without waiting on an asset.
 */
export interface ClientTestimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  /** Square-ish headshot. Falls back to an initial when absent. */
  photo?: string;
  /** Company mark, shown on a light plate so it reads on dark. */
  logo?: string;
  /** 96px square crop of `photo`, pre-cropped to photoPos — for avatar stacks. */
  avatar?: string;
  /** objectPosition for the headshot, when center crops badly. */
  photoPos?: string;
}

const TIMG = "/images/blogger-outreach";
// 96px square crops of the headshots above, for compact avatar stacks.
const AIMG = "/images/avatars";

export const CLIENT_TESTIMONIALS: ClientTestimonial[] = [
  {
    quote: "I'd been let down by link sellers before, so I came in skeptical. Indexed proved me wrong — real sites, proper vetting, DR from near zero to 20 in a few months. First link building that did what it said.",
    name: "Eric Provencio", role: "CEO", company: "Analytics Engineering",
    avatar: `${AIMG}/eric-provencio.webp`, photo: `${TIMG}/eric-provencio.webp`, photoPos: "50% 18%",
  },
  {
    quote: "We'd tried link building before and got burned. Indexed was different — real sites, real posts, and our product pages started ranking within months. It's the first SEO spend I've never questioned.",
    name: "Kate Gibson", role: "CEO", company: "Velvet & Valor",
    avatar: `${AIMG}/kate-gibson.webp`, photo: `${TIMG}/kate-gibson.png`, photoPos: "50% 30%", logo: `${TIMG}/logo-velvet-valor.png`,
  },
  {
    quote: "Indexed built a scalable search foundation that now drives pipeline and revenue, while reducing our dependence on paid acquisition.",
    name: "Alex Lomax", role: "CEO", company: "Upvolt Energy",
    avatar: `${AIMG}/alex-lomax.webp`, photo: `${TIMG}/alex-lomax.jpg`, photoPos: "50% 30%", logo: `${TIMG}/logo-upvolt.png`,
  },
  {
    quote: "The links actually land on sites our customers read. We can see the difference in organic bookings, not just in a rankings report.",
    name: "Emily Schwartz", role: "Marketing Operations Manager", company: "RecNation RV & Boat Storage",
    avatar: `${AIMG}/emily-schwartz.webp`, photo: `${TIMG}/emily-schwartz.png`, photoPos: "50% 30%", logo: `${TIMG}/logo-recnation.png`,
  },
  {
    quote: "Indexed has been an incredible partner — sharp, quick, and great communicators.",
    name: "Stefan Menker", role: "VP Marketing", company: "RecNation",
    avatar: `${AIMG}/stefan-menker.webp`, photo: `${TIMG}/stefan-menker.png`, photoPos: "50% 75%", logo: `${TIMG}/logo-recnation.png`,
  },
  {
    quote: "Patients now find us on Google before they find anyone else. The clinic's bookings from search have never been stronger.",
    name: "Richard Newman", role: "Medical Director", company: "Waverley Park Minor Surgery",
    avatar: `${AIMG}/richard-newman.webp`, photo: `${TIMG}/richard-newman.png`, photoPos: "18% center", logo: `${TIMG}/logo-waverley.png`,
  },
];

export interface BrandStep {
  n: string;
  title: string;
  desc: string;
}

export const NEXT_STEPS: BrandStep[] = [
  { n: "01", title: "We audit your search visibility gap", desc: "In a free 30-minute session, we show you exactly where your business ranks — and which competitors are capturing the leads that should be yours." },
  { n: "02", title: "We quantify the revenue opportunity", desc: "We estimate the pipeline impact of improving your rankings — not just clicks and impressions, but leads and closed revenue. You'll know the ROI before you commit." },
  { n: "03", title: "We execute the full-stack growth plan", desc: "If it's a fit, we get to work. Our Full-Stack Search Method™ covers technical SEO, content, links, local, and tracking — all measured against revenue." },
];

/** The site's canonical absolute URL (no trailing slash). */
export const SITE_URL = "https://www.joinindexed.com";
